import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";
import { hasPermission } from "@/lib/roles";
import {
  internalProfileLabel,
  parseAmountInput,
  type InternalProfileOption,
} from "../bill-shared";

interface ApprovalPolicy {
  _id: string;
  name: string;
  active: boolean;
  steps: { name?: string; approverProfileIds: string[] }[];
  criteria?: { vendorIds?: string[]; minAmount?: number; maxAmount?: number };
  priority: number;
}

interface EditableStep {
  key: number;
  name: string;
  approverProfileIds: string[];
}

let nextStepKey = 1;

export function PoliciesTab({
  authUserId,
  canManage,
}: {
  authUserId: string | undefined;
  canManage: boolean;
}) {
  const policies = useQuery(
    api.accountingBillMasters.listApprovalPolicies,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as ApprovalPolicy[] | undefined;

  const vendors = useQuery(
    api.accountingBillMasters.listVendors,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as { _id: string; displayName: string }[] | undefined;

  const internalProfiles = useQuery(
    api.accountingBillMasters.listInternalProfiles,
    authUserId ? { authUserId } : "skip",
  ) as InternalProfileOption[] | undefined;

  const createApprovalPolicy = useMutation(
    api.accountingBillMasters.createApprovalPolicy,
  );
  const updateApprovalPolicy = useMutation(
    api.accountingBillMasters.updateApprovalPolicy,
  );

  // Only profiles whose role can actually approve are offered as approvers;
  // the backend rejects any others.
  const approverOptions = React.useMemo(
    () =>
      (internalProfiles ?? []).filter((profile) =>
        hasPermission(profile.role, "accounting:approve"),
      ),
    [internalProfiles],
  );

  const profileNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of internalProfiles ?? []) {
      map.set(profile._id, internalProfileLabel(profile));
    }
    return map;
  }, [internalProfiles]);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<ApprovalPolicy | null>(null);
  const [name, setName] = React.useState("");
  const [priority, setPriority] = React.useState("100");
  const [criteriaVendorIds, setCriteriaVendorIds] = React.useState<string[]>(
    [],
  );
  const [minAmount, setMinAmount] = React.useState("");
  const [maxAmount, setMaxAmount] = React.useState("");
  const [steps, setSteps] = React.useState<EditableStep[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [updatingPolicyId, setUpdatingPolicyId] = React.useState<string | null>(
    null,
  );

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPriority("100");
    setCriteriaVendorIds([]);
    setMinAmount("");
    setMaxAmount("");
    setSteps([{ key: nextStepKey++, name: "", approverProfileIds: [] }]);
    setDialogOpen(true);
  };

  const openEdit = (policy: ApprovalPolicy) => {
    setEditing(policy);
    setName(policy.name);
    setPriority(String(policy.priority));
    setCriteriaVendorIds(policy.criteria?.vendorIds ?? []);
    setMinAmount(
      policy.criteria?.minAmount !== undefined
        ? String(policy.criteria.minAmount)
        : "",
    );
    setMaxAmount(
      policy.criteria?.maxAmount !== undefined
        ? String(policy.criteria.maxAmount)
        : "",
    );
    setSteps(
      policy.steps.map((step) => ({
        key: nextStepKey++,
        name: step.name ?? "",
        approverProfileIds: [...step.approverProfileIds],
      })),
    );
    setDialogOpen(true);
  };

  const toggleApprover = (stepKey: number, profileId: string) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.key !== stepKey) return step;
        const has = step.approverProfileIds.includes(profileId);
        return {
          ...step,
          approverProfileIds: has
            ? step.approverProfileIds.filter((id) => id !== profileId)
            : [...step.approverProfileIds, profileId],
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!authUserId) return;
    const priorityNumber = Number(priority);
    if (!Number.isFinite(priorityNumber)) {
      toast.error("Priority must be a number");
      return;
    }
    if (steps.length === 0 || steps.some((s) => s.approverProfileIds.length === 0)) {
      toast.error("Every step needs at least one approver");
      return;
    }
    const min = parseAmountInput(minAmount);
    const max = parseAmountInput(maxAmount);
    const hasCriteria =
      criteriaVendorIds.length > 0 || min !== undefined || max !== undefined;
    const payloadSteps = steps.map((step) => ({
      name: step.name.trim() || undefined,
      approverProfileIds: step.approverProfileIds,
    }));
    const criteria = hasCriteria
      ? {
          vendorIds:
            criteriaVendorIds.length > 0 ? criteriaVendorIds : undefined,
          minAmount: min,
          maxAmount: max,
        }
      : undefined;

    setIsSaving(true);
    try {
      if (editing) {
        await updateApprovalPolicy({
          authUserId,
          policyId: editing._id,
          name,
          steps: payloadSteps,
          criteria: criteria ?? null,
          priority: priorityNumber,
        });
        toast.success("Rule updated");
      } else {
        await createApprovalPolicy({
          authUserId,
          name,
          steps: payloadSteps,
          criteria,
          priority: priorityNumber,
        });
        toast.success("Rule created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save rule");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (policy: ApprovalPolicy) => {
    if (!authUserId || updatingPolicyId !== null) return;
    setUpdatingPolicyId(policy._id);
    try {
      await updateApprovalPolicy({
        authUserId,
        policyId: policy._id,
        active: !policy.active,
      });
      toast.success(policy.active ? "Rule deactivated" : "Rule activated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update rule",
      );
    } finally {
      setUpdatingPolicyId(null);
    }
  };

  const isLoading =
    policies === undefined ||
    vendors === undefined ||
    internalProfiles === undefined;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          Assigning a bill is the standard approval workflow. Use a rule only
          to route unassigned bills automatically by vendor, amount, or
          multiple steps. An explicit assignee always overrides these rules.
        </p>
        {canManage && (
          <Button size="sm" disabled={isLoading} onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New rule
          </Button>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : policies.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No advanced approval rules. Assign each bill to its final approver
          before submitting, or create a rule for automatic routing.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-20">Priority</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="w-32" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map((policy) => (
                <TableRow key={policy._id}>
                  <TableCell className="py-1.5 font-medium">
                    {policy.name}
                  </TableCell>
                  <TableCell className="py-1.5 tabular-nums">
                    {policy.priority}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-1.5 text-xs">
                    {policy.steps
                      .map(
                        (step, index) =>
                          `${step.name ?? `Step ${index + 1}`} (${step.approverProfileIds
                            .map((id) => profileNameById.get(id) ?? "Unknown")
                            .join(", ")})`,
                      )
                      .join(" → ")}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Badge variant={policy.active ? "secondary" : "outline"}>
                      {policy.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Edit rule"
                          disabled={updatingPolicyId !== null}
                          onClick={() => openEdit(policy)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={updatingPolicyId !== null}
                          onClick={() => handleToggleActive(policy)}
                        >
                          {updatingPolicyId === policy._id
                            ? "Updating…"
                            : policy.active
                              ? "Deactivate"
                              : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!isSaving) setDialogOpen(open);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit approval rule" : "New approval rule"}
            </DialogTitle>
            <DialogDescription>
              Steps run in order; each step needs at least one approver with
              approval rights.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="policy-name">Name</Label>
                <Input
                  id="policy-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="policy-priority">
                  Priority (lower wins)
                </Label>
                <Input
                  id="policy-priority"
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Criteria (optional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="policy-min-amount"
                    className="text-muted-foreground text-xs font-normal"
                  >
                    Min amount
                  </Label>
                  <Input
                    id="policy-min-amount"
                    type="number"
                    inputMode="decimal"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="policy-max-amount"
                    className="text-muted-foreground text-xs font-normal"
                  >
                    Max amount
                  </Label>
                  <Input
                    id="policy-max-amount"
                    type="number"
                    inputMode="decimal"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">
                  Limit to vendors (none = any vendor):
                </p>
                <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-md border p-2">
                  {(vendors ?? []).map((vendor) => (
                    <label
                      key={vendor._id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={criteriaVendorIds.includes(vendor._id)}
                        onCheckedChange={(checked) =>
                          setCriteriaVendorIds((prev) =>
                            checked === true
                              ? [...prev, vendor._id]
                              : prev.filter((id) => id !== vendor._id),
                          )
                        }
                      />
                      {vendor.displayName}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Steps (in order)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setSteps((prev) => [
                      ...prev,
                      { key: nextStepKey++, name: "", approverProfileIds: [] },
                    ])
                  }
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add step
                </Button>
              </div>
              {steps.map((step, index) => (
                <div key={step.key} className="space-y-2 rounded-md border p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {index + 1}.
                    </span>
                    <Input
                      value={step.name}
                      onChange={(e) =>
                        setSteps((prev) =>
                          prev.map((s) =>
                            s.key === step.key
                              ? { ...s, name: e.target.value }
                              : s,
                          ),
                        )
                      }
                      placeholder={`Step ${index + 1} name (optional)`}
                      className="h-8 text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Remove step"
                      disabled={steps.length === 1}
                      onClick={() =>
                        setSteps((prev) =>
                          prev.filter((s) => s.key !== step.key),
                        )
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="max-h-28 space-y-0.5 overflow-y-auto pl-5">
                    {approverOptions.length === 0 ? (
                      <p className="text-muted-foreground text-xs">
                        No eligible approvers found.
                      </p>
                    ) : (
                      approverOptions.map((profile) => (
                        <label
                          key={profile._id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Checkbox
                            checked={step.approverProfileIds.includes(
                              profile._id,
                            )}
                            onCheckedChange={() =>
                              toggleApprover(step.key, profile._id)
                            }
                          />
                          {internalProfileLabel(profile)}
                          <span className="text-muted-foreground text-xs">
                            ({profile.role})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isSaving}
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
              {isSaving ? "Saving…" : editing ? "Save rule" : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
